import { Key, useEffect, useState } from "react";
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader } from "baseui/modal";
import { FormControl } from "baseui/form-control";
import {
  useCreateUserMutation,
  useGetUsersQuery,
  useUpdateUsersAppRolesMutation,
} from "../../../services/users/userApi";
import { Input } from "baseui/input";
import { validateEmail, validateGuid } from "../../../utils/utils";
import { useReportMutationState } from "../../../hooks/useReportMutationState";
import { Banner } from "baseui/banner";
import { SegmentedControl, Segment } from "baseui/segmented-control";
import { FormattedMessage, useIntl } from "react-intl";
import { useDebounce } from "../../../hooks/useDebounce";
import { AccessRoles, useHasRole } from "../../../hooks/useHasRole";
import { useAddUserToTenantMutation } from "../../../services/resourceGroups/tenantApi";
import { getRolesAsStrings, UpdateRolesFormControl } from "../updateRolesDialog/UpdateRolesDialog";
import { Panel } from "baseui/accordion";
import { Button } from "baseui/button";

type InviteUserDialogProps = {
  tenantId: string;
  isOpen: boolean;
  setIsOpen: any;
};

type InviteUserDialogState = {
  name: string | undefined;
  email: string | undefined;
  appId: string | undefined;
  isNameValid: boolean | undefined;
  isMailValid: boolean | undefined;
  isAppIdValid: boolean | undefined;
};

const InviteUserDialog: React.FC<InviteUserDialogProps> = (props) => {
  const { tenantId, isOpen, setIsOpen } = props;
  const intl = useIntl();
  const [activeKey, setActiveKey] = useState<Key>("0");
  const [create, createResult] = useCreateUserMutation();
  useReportMutationState(createResult, () => close());

  const [addUserToTenant, addUserToTenantResult] = useAddUserToTenantMutation();
  useReportMutationState(addUserToTenantResult, () => {
    if (!modifyRoles) close();
  });
  const [updateUserAppRoles, updateUserAppRolesResult] = useUpdateUsersAppRolesMutation();
  useReportMutationState(updateUserAppRolesResult, () => close());

  const [state, setState] = useState<InviteUserDialogState>({
    name: undefined,
    email: undefined,
    appId: undefined,
    isNameValid: undefined,
    isMailValid: undefined,
    isAppIdValid: undefined,
  });
  const debouncedMail = useDebounce(state.email, 500);
  const { data: existingUsers, isLoading } = useGetUsersQuery({ mail: debouncedMail ?? "" }, { skip: !debouncedMail });
  const isSa = useHasRole(AccessRoles.SuperAdmin);
  const inputsValid = state.isNameValid && (state.isMailValid || state.isAppIdValid);

  const userExists = !isLoading && (existingUsers ?? []).length > 0;
  const existingUser = userExists ? (existingUsers ?? [])[0] : undefined;
  const close = () => setIsOpen(false);

  const handleNameChanged = (newName: string) => {
    setState((prev: any) => {
      return {
        ...prev,
        name: newName,
        isNameValid: newName && newName.length > 3,
      };
    });
  };

  const handleMailChanged = (newMail: string) => {
    setState((prev: any) => {
      return {
        ...prev,
        email: newMail,
        isMailValid: validateEmail(newMail),
      };
    });
  };
  const handleAppIdChanged = (newAppId: string) => {
    setState((prev: any) => {
      return {
        ...prev,
        appId: newAppId,
        isMailValid: validateGuid(newAppId),
      };
    });
  };

  const handleOk = async () => {
    if (!existingUser) {
      // Consider using addUserToTenant instead of writing it in targetRelations
      await create({
        email: state.email ?? "",
        displayName: state.name ?? "",
        appId: state.appId ?? "",
        targetRelations: [{ sourceId: tenantId, relationName: "HasMember" }],
        roleNames: modifyRoles ? getRolesAsStrings(selectedRoles) : undefined,
      });
    } else {
      await addUserToTenant({ tenantId: tenantId, userId: existingUser.id });
      if (modifyRoles)
        await updateUserAppRoles({ userId: existingUser.id, roleNames: getRolesAsStrings(selectedRoles) });
    }
  };

  // Roles ---

  const [modifyRoles, setModifyRoles] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([0]);
  useEffect(() => {
    if (!modifyRoles) {
      setSelectedRoles([0]); // reset to default
    }
  }, [modifyRoles]);

  return (
    <Modal onClose={close} isOpen={isOpen}>
      <ModalHeader>
        <FormattedMessage id="inviteUser" defaultMessage="Invite User" />
      </ModalHeader>
      <ModalBody>
        <SegmentedControl
          activeKey={activeKey}
          onChange={({ activeKey }) => {
            setActiveKey(activeKey);
          }}
          overrides={{
            Root: {
              style: ({ $theme }) => ({
                width: "100%",
              }),
            },
          }}>
          <Segment
            label={<FormattedMessage id="person" defaultMessage="Person" />}
            description={<FormattedMessage id="addPersonDescription" defaultMessage="Add a Human being" />}
          />
          <Segment
            disabled={!isSa}
            label={<FormattedMessage id="externalService" defaultMessage="External Service" />}
            description={
              <FormattedMessage id="addExternalServiceDescription" defaultMessage="Add an external Service" />
            }
          />
        </SegmentedControl>
        {userExists && (
          <Banner
            title={
              <>
                {`'${existingUser?.id}' `}
                <FormattedMessage id="alreadyExists" defaultMessage="Already exists" />
              </>
            }>
            <FormattedMessage id="userWillBeLinkedToTenant" defaultMessage="It will be linked to this tenant." />
          </Banner>
        )}
        {activeKey === "0" && (
          <FormControl
            label={<FormattedMessage id="email" defaultMessage="E-Mail" />}
            caption={<FormattedMessage id="email.caption" defaultMessage="Enter the users e-mail" />}
            error={!state.isMailValid}>
            <>
              <Input
                value={state.email}
                onChange={(e) => {
                  handleMailChanged(e.target.value);
                }}
                placeholder="someone@factory-ag.com"
                clearable
                clearOnEscape
                disabled={state.appId !== undefined && state.appId !== ""}
              />
            </>
          </FormControl>
        )}
        {activeKey === "1" && (
          <FormControl
            label="App Id"
            caption="Enter the App Object Id for App Registrations"
            error={!state.isMailValid}>
            <>
              <Input
                value={state.appId}
                onChange={(e) => {
                  handleAppIdChanged(e.target.value);
                }}
                placeholder="12c34567-d000-040c-a0d9-123456789123"
                clearable
                clearOnEscape
                disabled={userExists || (state.email !== undefined && state.email !== "")}
              />
            </>
          </FormControl>
        )}
        <FormControl
          label={<FormattedMessage id="displayName" defaultMessage="Display Name" />}
          caption={<FormattedMessage id="username.caption" defaultMessage="Enter a descriptive Username to display" />}
          error={!state.isNameValid && !userExists}>
          <>
            <Input
              value={userExists ? existingUser?.displayName : state.name}
              onChange={(e) => {
                handleNameChanged(e.target.value);
              }}
              placeholder={intl.formatMessage({ id: "username.placeholder", defaultMessage: "Enter a name ..." })}
              clearable
              clearOnEscape
              disabled={userExists}
            />
          </>
        </FormControl>

        <Panel
          title={<FormattedMessage id="inviteUser.setRoles" defaultMessage="Set user access roles" />}
          onChange={() => setModifyRoles(!modifyRoles)}
          expanded={modifyRoles}>
          <UpdateRolesFormControl selected={selectedRoles} setSelected={setSelectedRoles} />
        </Panel>
        {modifyRoles && userExists && (
          <Banner
            title={
              <FormattedMessage id="inviteUser.RolesWillBeModified" defaultMessage="Existing Roles will be modified" />
            }>
            <FormattedMessage
              id="inviteUser.RolesWillBeModified2"
              defaultMessage="This will update the users current role-set according to your selection"
            />
            <br />
            <Button title="Discard role change" onClick={() => setModifyRoles(false)} size="compact">
              <FormattedMessage id="inviteUser.discardRoleChange" defaultMessage="Discard role change" />
            </Button>
          </Banner>
        )}
      </ModalBody>
      <ModalFooter>
        <ModalButton kind="tertiary" onClick={close}>
          <FormattedMessage id="cancel" defaultMessage={"Cancel"} />
        </ModalButton>
        <ModalButton
          disabled={(!inputsValid && !existingUser) || isLoading}
          isLoading={createResult.isLoading}
          onClick={handleOk}>
          <FormattedMessage id="ok" defaultMessage="Okay" />
        </ModalButton>
      </ModalFooter>
    </Modal>
  );
};

export default InviteUserDialog;
